/* eslint-disable react/no-danger */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-nested-ternary */
import usePagination from '@/components/client/Pagination/UsePagination'
import useProfessionList from '@/hooks/useProfessionsList'
import {
  FindHeroImagesDocument,
  ListCoursesDocument,
} from '@/types/graphql/types'
import { useQuery } from '@apollo/client'
import { EVENT_FOLDER, SETTINGS_IDS } from 'db'
import { useRouter } from 'next/router'
import { useState } from 'react'

type ImgType = {
  url: string
  hidden: boolean
}[]

const useEvents = ({
  titleSearch,
  setTitleSearch,
}: {
  titleSearch: string
  setTitleSearch: (value: string) => void
}) => {
  const { back, next, page } = usePagination()
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [selectedProfession, setSelectedProfession] = useState<Array<number>>(
    [],
  )
  const [selectedDeliveryMethods, setSelectedDeliveryMethods] = useState<
    number[]
  >([])
  const [selectedPricingPlans, setSelectedPricingPlans] = useState<number[]>([])

  const { query } = useRouter()

  const handleProfessionChange = (id: number) => {
    if (selectedProfession.includes(id)) {
      setSelectedProfession(selectedProfession.filter(item => item !== id))
    } else {
      setSelectedProfession([...selectedProfession, id])
    }
  }

  const handleFilterChange = (filterId: string, value: number) => {
    const setters = {
      profession: setSelectedProfession,
      methodOfDelivery: setSelectedDeliveryMethods,
      pricingPlan: setSelectedPricingPlans,
    }

    const selectedValues = {
      profession: selectedProfession,
      methodOfDelivery: selectedDeliveryMethods,
      pricingPlan: selectedPricingPlans,
    }

    const currentSelected =
      selectedValues[filterId as keyof typeof selectedValues]
    const setter = setters[filterId as keyof typeof setters]

    if (currentSelected.includes(value)) {
      setter(currentSelected.filter(item => item !== value))
    } else {
      setter([...currentSelected, value])
    }
  }

  const handleTitleChange = (title: string) => {
    setTitleSearch(title)
  }

  const folderId = query.archived
    ? EVENT_FOLDER.archive.id
    : EVENT_FOLDER.published.id
  const visibleOnArchive = query.archived
    ? {
        equals: true,
      }
    : undefined
  // fetch archived or published events
  const { data, loading } = useQuery(ListCoursesDocument, {
    variables: {
      where: {
        AND: [
          // Profession filter
          {
            OR: selectedProfession.length
              ? [
                  {
                    targetedAudiences: {
                      some: {
                        Specialties: {
                          some: {
                            professionId: {
                              in: [
                                ...selectedProfession.map(p => Math.round(p)),
                              ],
                            },
                          },
                        },
                      },
                    },
                  },
                  { isAllProfessionsTargeted: { equals: true } },
                ]
              : undefined,
          },
          // Method of delivery filter
          selectedDeliveryMethods.length
            ? {
                OR: selectedDeliveryMethods.map(method => {
                  switch (method) {
                    case 11: // Online CME
                      return {
                        AND: [
                          { methodOfDeliveryId: { equals: 1 } },
                          { isAccredited: { equals: true } },
                        ],
                      }
                    case 21: // Onsite CME
                      return {
                        AND: [
                          { methodOfDeliveryId: { equals: 2 } },
                          { isAccredited: { equals: true } },
                        ],
                      }
                    case 31: // SHA
                      return {
                        isAccreditedSHA: { equals: true },
                      }
                    default: // Regular delivery methods (1 or 2)
                      return {
                        AND: [
                          { methodOfDeliveryId: { equals: method } },
                          { isAccredited: { equals: false } },
                          { isAccreditedSHA: { equals: false } },
                        ],
                      }
                  }
                }),
              }
            : {},
          // Pricing plan filter
          selectedPricingPlans.length
            ? {
                pricingPlan: {
                  pricingPlanTypeId: { in: selectedPricingPlans },
                },
              }
            : {},
          // Folder and archive filters
          {
            folderId: {
              equals: folderId,
            },
          },
          visibleOnArchive ? { visibleOnArchive } : {},
        ].filter(Boolean),
      },
      skip: page * 12,
      take: 12,
    },
  })

  const { professionList } = useProfessionList({ showSpecialties: true })

  const filters = [
    {
      id: 'profession',
      name: 'Profession',
      selected: selectedProfession,
      options:
        professionList
          ?.filter(
            p =>
              p.titleEn?.trim().toLowerCase().includes(titleSearch) ||
              p.titleAr?.trim().toLowerCase().includes(titleSearch),
          )
          ?.map(item => ({
            value: item.id,
            label: item.titleEn,
          })) ?? [],
    },
    {
      id: 'methodOfDelivery',
      name: 'Method Of Delivery',
      selected: selectedDeliveryMethods,
      options: [
        { label: 'Online CME', value: 11 }, // Online + CME
        { label: 'Onsite CME', value: 21 }, // Onsite + CME
        { label: 'SHA', value: 31 }, // SHA specific
        { label: 'Online', value: 1 }, // Regular Online
        { label: 'Onsite', value: 2 }, // Regular Onsite
      ],
    },
    {
      id: 'pricingPlan',
      name: 'Pricing Plan',
      selected: selectedPricingPlans,
      options: [
        { label: 'Free', value: 1 },
        { label: 'Paid', value: 2 },
      ],
    },
  ]
  // fetch hero images
  const { data: imagesData } = useQuery(FindHeroImagesDocument)
  const heroImages = (imagesData?.findHeroImages?.find(
    item => item?.id === SETTINGS_IDS.heroImages.events,
  )?.value?.eventImages ?? []) as ImgType
  const urls: string[] = heroImages
    .filter(e => e.hidden === false)
    .map(e => e.url)

  return {
    urls,
    filters,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    data,
    loading,
    handleFilterChange,
    handleTitleChange,
    next,
    back,
    page,
  }
}

export default useEvents
